import {usePlusLoadExpected} from '@hconnect/tuc-processes/fields/plusLoad/usePlusLoadExpected'
import {useQuantity} from '@hconnect/tuc-processes/fields/quantity/useQuantity'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement'
import {Alert, ControlledSwitch, Kpi} from '@hm/fundamental'
import {Box, Typography, Collapse} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {trackEvent} from '../../../../../../common/analytics'
import {RadioButtonGroup} from '../../../../../../Molecules/RadioButton'
import {
  FullLoadsSection,
  QuantityInput
} from '../../../../shared/order-editor/components/fields/Quantity'
import {useQuantityCorrection} from '../../../../shared/order-editor/components/fields/Quantity/useQuantityCorrection'

/**
 * Quantity step for orders that have started but don't have plus load enabled.
 * Allows changing the main order quantity and toggling plus load expectation.
 * Supports both quantity (m³) and fullLoads (truck-based) modes.
 */
export const OrderManagementAfterStartedQuantityStep: React.FC = () => {
  const {t} = useTranslation()

  // Use the form context to get validation errors
  const {formState} = useOrderProcessFormContext()

  // Use the specialized hooks from orderProcessesPackage
  const quantityReturn = useQuantity()
  const {quantityConfiguration, setType, isFullLoadEnabled} = quantityReturn

  // Plus Load Expected flag
  const {plusLoad, setPlusLoad} = usePlusLoadExpected()
  const plusLoadDisabled = useChangeableField('plusLoadExpected').state === 'notChangeable'
  const quantityDisabled = useChangeableField('quantity').state === 'notChangeable'

  // Use override range if provided, otherwise use default configuration
  const range = quantityConfiguration
  const {min: minValue, max: maxValue, step, uom: quantityUom} = range

  // Get validation errors for all quantity-related fields
  const quantityError = (formState.errors?.quantity as {message?: string} | undefined)?.message
  const plusLoadError = formState.errors?.plusLoadExpected?.message

  // Use custom hook for auto-correction management (only relevant in quantity mode)
  const {correctionMessage, hasCorrection, handleAutoCorrect} = useQuantityCorrection({
    minValue,
    maxValue,
    step,
    quantityUom
  })

  // Handle quantity change with analytics tracking (only used in quantity mode)
  const handleQuantityChange = React.useCallback(
    (newQuantity: number) => {
      if (quantityReturn.type !== 'volume') return
      trackEvent('rmcQuantityChanged', {
        quantity: newQuantity,
        unit: quantityUom,
        businessLine: 'RMC'
      })

      void quantityReturn.setVolume(newQuantity)
    },
    [quantityReturn, quantityUom]
  )

  // Handle mode switch via ControlledSwitch
  const handleModeChange = React.useCallback(
    (value: string | number) => {
      const mode = value as 'volume' | 'fullLoads'
      trackEvent('rmcQuantityModeChanged', {
        mode,
        businessLine: 'RMC',
        context: 'orderManagement'
      })
      setType(mode)
    },
    [setType]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        padding: 0
      }}
    >
      {/* Mode Toggle - only shown when full loads feature is enabled */}
      {isFullLoadEnabled && (
        <Box
          sx={{
            '& > div': {width: '100%'},
            '& > div > button': {flex: 1}
          }}
        >
          <ControlledSwitch
            value={quantityReturn.type}
            options={[
              {
                label: t('orderIntake.rmc.steps.quantity.byQuantity', 'By quantity'),
                value: 'volume'
              },
              {
                label: t('orderIntake.rmc.steps.quantity.byFullLoads', 'By full loads'),
                value: 'fullLoads'
              }
            ]}
            onChange={handleModeChange}
            data-cy="quantity-mode-switch"
          />
        </Box>
      )}

      {/* Quantity Details */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '48px', // 12 * 4px = 48px from design
          alignItems: 'center'
        }}
      >
        {/* Quantity Input Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px', // 4 * 4px = 16px from design
            width: '100%'
          }}
        >
          {/* Mode-specific content */}
          {quantityReturn.type === 'volume' ? (
            <>
              <QuantityInput
                value={quantityReturn.volume}
                onChange={handleQuantityChange}
                range={range}
                onAutoCorrect={handleAutoCorrect}
                disabled={quantityDisabled}
                data-test-id="rmc-quantity-input"
              />

              {/* Quantity validation error display */}
              {quantityError && (
                <Box sx={{mt: 1}}>
                  <Typography color="error" variant="body2">
                    {quantityError}
                  </Typography>
                </Box>
              )}

              {/* Correction notification - show when an auto-correction happens */}
              <Collapse in={hasCorrection}>
                <Alert variant="info" data-test-id="quantity-correction-alert">
                  {correctionMessage}
                </Alert>
              </Collapse>

              {/* KPI Display */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px', // 4 * 4px = 16px from design
                  width: '100%'
                }}
              >
                <Kpi
                  className="flex-1"
                  size="xsmall"
                  label={t(
                    'orderIntake.rmc.orderManagement.quantityChange.afterStarted.minValue',
                    'Min. value'
                  )}
                  value={Math.min(range.min, range.closeAt ?? range.min)}
                  unit={range.uom}
                />
                <Kpi
                  className="flex-1"
                  size="xsmall"
                  label={t(
                    'orderIntake.rmc.orderManagement.quantityChange.afterStarted.maxValue',
                    'Max. value'
                  )}
                  value={range.max}
                  unit={range.uom}
                />
              </Box>

              {/* Hint about larger changes */}
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#6B7280'
                }}
              >
                {t(
                  'orderIntake.rmc.orderManagement.quantityChange.afterStarted.largerChangeHint',
                  'If you need a larger quantity change, contact dispatch.'
                )}
              </Typography>
            </>
          ) : (
            <>
              <FullLoadsSection quantityReturn={quantityReturn} />

              {/* Full loads validation error display */}
              {quantityError && (
                <Box sx={{mt: 1}}>
                  <Typography color="error" variant="body2">
                    {quantityError}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Plus Load Toggle */}
          <Box data-test-id="plus-load-section">
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '24px',
                color: '#29333D',
                marginBottom: '20px'
              }}
            >
              {t(
                'orderIntake.rmc.orderManagement.quantityChange.afterStarted.plusloadNeeded',
                'Will you need a plus load?'
              )}
            </Typography>

            <RadioButtonGroup
              value={plusLoad}
              disabled={plusLoadDisabled}
              onValueChange={setPlusLoad}
              options={[
                {
                  label: t(
                    'orderIntake.rmc.orderManagement.quantityChange.afterStarted.plusloadNo',
                    'No'
                  ),
                  value: false,
                  testId: 'plus-load-toggle-no'
                },
                {
                  label: t(
                    'orderIntake.rmc.orderManagement.quantityChange.afterStarted.plusloadYes',
                    'Yes'
                  ),
                  value: true,
                  testId: 'plus-load-toggle-yes'
                }
              ]}
              direction="column"
              spacing={1}
              variant="neutral"
              testId="plus-load-radio-group"
            />
          </Box>

          {/* Plus Load validation error display */}
          {plusLoadError && (
            <Box sx={{mt: 1}}>
              <Typography color="error" variant="body2">
                {plusLoadError}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
